import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { CacheProvider, css, Global } from "@emotion/react";
import { ContextValue } from "./types/ThemeContextValue";
import { ThemeProviderProps } from "./types/ThemeProviderProps"
import { useTheme } from "./useTheme"
import { generateVars } from "./utils/generateVars";
import { mergeVars } from "./utils/mergeVars";
import { NormalizeCSS } from "./themes/NormalizeCSS";
import { DarkTheme } from "./themes/DarkTheme";
import { LightTheme } from "./themes/LightTheme";
import { Variables } from "./themes/Variables";
import { defaultEmotionCache } from "./default-emotion-cache";



export const ThemeContext = createContext<ContextValue | undefined>(undefined)

export const useThemeContext = () => {
  return useContext(ThemeContext) as ContextValue
}

export const ThemeProvider = (
  {
    appearance: defaultAppearance = "dark",
    matchPrefers,
    theme: defaultTheme,
    vars: defaultVars,
    children
  }: PropsWithChildren<ThemeProviderProps>
) => {
  const theme = useTheme()

  const cssVars = useMemo(() => {
    return css`
      :root {
        ${generateVars(!defaultVars ? Variables : mergeVars(defaultVars, Variables))}
      }
    `
  }, [defaultVars])

  const contextValues: ContextValue = useMemo(() => {
    return {
      appearance: theme.appearance,
      setAppearance: theme.setAppearance,
      doMatchPrefers: theme.doMatchPrefers,
      toggleAppearance: theme.toggleAppearance,
    }
  }, [theme])

  const cssTheme = useMemo(() => {
    const dark = generateVars(
      !defaultTheme || !defaultTheme.dark
      ? DarkTheme
      : mergeVars(defaultTheme.dark, DarkTheme)
    )
    const light = generateVars(
      !defaultTheme || !defaultTheme.light
        ? LightTheme
        : mergeVars(defaultTheme.light, LightTheme)
    )

    return css`
      html[data-color-scheme=dark] {
        ${dark}
      }

      html[data-color-scheme=light] {
        ${light}
      }
    `
  }, [defaultTheme])

  useEffect(() => {
    theme.setAppearance(defaultAppearance)

    if (matchPrefers) {
      theme.doMatchPrefers()
    }

  }, [defaultAppearance, matchPrefers])

  return (
    <ThemeContext.Provider value={contextValues}>
      <NormalizeCSS />
      <Global styles={cssVars}/>
      <Global styles={cssTheme}/>
      <CacheProvider value={defaultEmotionCache}>
        {children}
      </CacheProvider>
    </ThemeContext.Provider>
  );
}

